/**
 * The organization — the caller's own, and the platform-operator surface.
 *
 * Replaces `Account.GetOrganizationSnapshot` and `AccountUser.UpdateAccountInfo`,
 * plus the pacmny-be account reads folded into `getAllData`.
 *
 * There is no organization id in any of the first three calls, and that is the
 * point: `accountnumber varchar(253)` used to travel in the body of every
 * request and the server believed it. Tenancy now comes from the access token,
 * so "my organization" is not a parameter a caller can change.
 *
 * The `/organizations/{organization}` routes are the platform-operator
 * exception — `organizations` is the one table the tenant scope cannot apply to
 * — and they carry a platform-operator check of their own (the flag, inside
 * the platform tenant: `User::isPlatformOperator`). An ordinary administrator
 * calling them gets a 403, whatever the id.
 *
 * Billing terms live in `billing.js` and licence-verification policy in
 * `licenses.js`; both are separate rights from editing the organization's name.
 */

import api from './client';

// --- the caller's own organization -----------------------------------------

export function getOrganization() {
  return api.get('/organization');
}

/**
 * Name, address, counts and feature flags in one read — what the app needs at
 * boot. It replaces the account slice of pacmny-be's `getAllData`, which
 * returned the patient's chart alongside it and so could never be cached.
 */
export function getOrganizationSnapshot() {
  return api.get('/organization/snapshot');
}

export function updateOrganization(patch) {
  return api.patch('/organization', patch);
}

/**
 * Verify a business EIN during onboarding. An unconfigured provider answers 503
 * rather than "matched": a verification that silently succeeds when it cannot
 * run is worse than none, because the operator believes it ran.
 */
export function lookupBusinessEin(params) {
  return api.get('/ein-lookups', params);
}

// --- platform operator -----------------------------------------------------
//
// Platform Administration's tenant screens (Trello nRhCnuT0, qt2p8aSU). Each
// needs `platform:tenants`, which only an operator of the platform tenant can
// hold, and the API asserts the operator besides.

/**
 * The customer organizations, newest first, each with its first
 * administrator and where their invitation stands. `params` takes the list
 * conventions: `page`, `per_page`, `filter[name]`, `filter[status]`, `sort`.
 */
export function listOrganizations(params = {}) {
  return api.list('/organizations', params);
}

/** The kinds of organization a tenant can be, for the create form. */
export function listOrganizationTypes() {
  return api.get('/organization-types');
}

/**
 * Tenant onboarding. The first administrator is invited by the API after the
 * tenant commits; the answer reports them `invited`, with `invited_at` null
 * until the authorization server has accepted the invitation.
 */
export function createOrganization(payload) {
  return api.post('/organizations', payload);
}

/** Send the first administrator's invitation again (`sent`, `already_sent` or `undelivered`). */
export function resendAdministratorInvitation(organizationId) {
  return api.post(`/organizations/${organizationId}/administrator-invitations`);
}

export function getOrganizationById(organizationId) {
  return api.get(`/organizations/${organizationId}`);
}

export function updateOrganizationById(organizationId, patch) {
  return api.patch(`/organizations/${organizationId}`, patch);
}

export default {
  getOrganization, getOrganizationSnapshot, updateOrganization, lookupBusinessEin,
  listOrganizations, listOrganizationTypes, createOrganization, resendAdministratorInvitation,
  getOrganizationById, updateOrganizationById,
};
